import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { MainLayout } from "components/layout/MainLayout";
import SunEditor from "suneditor-react";
import "suneditor/dist/css/suneditor.min.css";
import {
  Card,
  CardHeader,
  CardBody,
  Grid,
  GridItem,
  Input,
  Button,
  Checkbox,
  ColorPicker,
} from "components/ui";
import { packagesService } from "services/packages.service";
import { useToast } from "hooks/useToast";
import { ROUTES } from "constants/index";
import { useFieldValidation } from "hooks/useFieldValidation";
import { LogoIntelligenceResult } from "@/utils/logoIntelligence";
import { PackageDetail, getDetailByDuration } from "types/package";

type PackageFormState = {
  title: string;
  name: string;
  description: string;
  numberOfUsers: string;
  color: string;
  isIndividual: boolean;
  monthlyValidityDays: string;
  monthlyPrice: string;
  yearlyValidityDays: string;
  yearlyPrice: string;
};

export const PackageFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const isEdit = Boolean(id) && !location.pathname.endsWith("/view");
  const isView = location.pathname.endsWith("/view");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const [form, setForm] = useState<PackageFormState>({
    title: "",
    name: "",
    description: "",
    numberOfUsers: "",
    monthlyValidityDays: "30",
    monthlyPrice: "0",
    yearlyValidityDays: "365",
    yearlyPrice: "0",
    color: "#000000",
    isIndividual: false,
  });

  const [logoInsights, setLogoInsights] = useState<
    LogoIntelligenceResult | undefined
  >();

  const validators = useMemo(
    () => ({
      name: (state: PackageFormState) =>
        !state.name?.trim() ? "Package name is required" : undefined,
      description: (state: PackageFormState) =>
        !state.description?.replace("<p><br></p>", "").trim()
          ? "Description is required"
          : undefined,
      color: (state: PackageFormState) =>
        !state.color?.trim() ? "Color is required" : undefined,
      numberOfUsers: (state: PackageFormState) => {
        if (!state.numberOfUsers?.trim()) return "Number of users is required";
        const value = Number(state.numberOfUsers);
        if (isNaN(value) || value <= 0) return "Must be a positive number";
        return undefined;
      },
      monthlyValidityDays: (state: PackageFormState) => {
        if (!state.monthlyValidityDays?.trim())
          return "Monthly validity (days) is required";
        const value = Number(state.monthlyValidityDays);
        if (isNaN(value) || value <= 0) return "Must be a positive number";
        return undefined;
      },
      monthlyPrice: (state: PackageFormState) => {
        if (!state.monthlyPrice?.trim()) return "Monthly price is required";
        const value = Number(state.monthlyPrice);
        if (isNaN(value) || value < 0) return "Must be zero or a positive number";
        return undefined;
      },
      yearlyValidityDays: (state: PackageFormState) => {
        if (!state.yearlyValidityDays?.trim())
          return "Yearly validity (days) is required";
        const value = Number(state.yearlyValidityDays);
        if (isNaN(value) || value <= 0) return "Must be a positive number";
        return undefined;
      },
      yearlyPrice: (state: PackageFormState) => {
        if (!state.yearlyPrice?.trim()) return "Yearly price is required";
        const value = Number(state.yearlyPrice);
        if (isNaN(value) || value < 0) return "Must be zero or a positive number";
        return undefined;
      },
    }),
    []
  );

  const { errors, validateAll, revalidateFields, resetErrors } =
    useFieldValidation<PackageFormState>(validators);

  const isNumericField = (field: keyof PackageFormState) =>
    field === "numberOfUsers" ||
    field === "monthlyValidityDays" ||
    field === "monthlyPrice" ||
    field === "yearlyValidityDays" ||
    field === "yearlyPrice";

  useEffect(() => {
    const load = async () => {
      if ((isEdit || isView) && id) {
        setIsLoading(true);
        setError(null);
        try {
          const pkg = await packagesService.getById(id);
          const monthly = getDetailByDuration(pkg, false);
          const yearly = getDetailByDuration(pkg, true);
          setForm({
            title: pkg.title || "",
            name: pkg.name || "",
            description: pkg.description || "",
            numberOfUsers: String(pkg.numberOfUsers ?? ""),
            monthlyValidityDays: String(monthly?.validityDays ?? 30),
            monthlyPrice: String(monthly?.price ?? ""),
            yearlyValidityDays: String(yearly?.validityDays ?? 365),
            yearlyPrice: String(yearly?.price ?? ""),
            color: pkg.color || "#000000",
            isIndividual: pkg.isIndividual || false,
          });
          resetErrors();
        } catch (e: any) {
          const friendly =
            "An error occurred on the server. Please contact the administrator.";
          setError(e.message ?? friendly);
          toast.error(e.message ?? friendly);
        } finally {
          setIsLoading(false);
        }
      }
    };
    load();
  }, [id, isEdit, isView, resetErrors, toast]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    const fieldName = name as keyof PackageFormState;
    setForm((prev) => {
      const nextValue = isNumericField(fieldName)
        ? value.replace(/[^0-9.]/g, "")
        : value;
      const next = { ...prev, [fieldName]: nextValue } as PackageFormState;
      revalidateFields(next, [fieldName]);
      return next;
    });
  };

  const handleColorChange = (field: "color") => (color: string) => {
    setForm((prev) => ({ ...prev, [field]: color }));
  };

  const palettePresets = useMemo(() => {
    const defaults = ["#ffffff", "#C0C0C0", "#f44336", "#FFD700", "#E5E4E2"];
    const aiColors = (logoInsights?.palette ?? [])
      .map((color) => color?.trim().toLowerCase())
      .filter((color): color is string => Boolean(color));
    const unique = Array.from(new Set([...aiColors, ...defaults]));
    return unique.slice(0, 10);
  }, [logoInsights]);

  const handleChecked = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: checked }));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const { name } = e.currentTarget;
    if (
      name === "numberOfUsers" ||
      name === "monthlyValidityDays" ||
      name === "monthlyPrice" ||
      name === "yearlyValidityDays" ||
      name === "yearlyPrice"
    ) {
      if ([8, 9, 27, 13, 46, 35, 36, 37, 38, 39, 40].includes(e.keyCode))
        return;
      if (
        (e.ctrlKey || e.metaKey) &&
        [65, 67, 86, 88, 90].includes(e.keyCode)
      )
        return;
      if (
        (e.shiftKey || e.keyCode < 48 || e.keyCode > 57) &&
        (e.keyCode < 96 || e.keyCode > 105) &&
        e.keyCode !== 190
      )
        e.preventDefault();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const { name } = e.currentTarget;
    if (
      name === "numberOfUsers" ||
      name === "monthlyValidityDays" ||
      name === "monthlyPrice" ||
      name === "yearlyValidityDays" ||
      name === "yearlyPrice"
    ) {
      e.preventDefault();
      const pastedText = e.clipboardData.getData("text");
      const numericValue = pastedText.replace(/[^0-9.]/g, "");
      setForm((prev) => {
        const fieldName = name as keyof PackageFormState;
        const next = { ...prev, [fieldName]: numericValue };
        revalidateFields(next, [fieldName]);
        return next;
      });
    }
  };

  const onSubmit = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const validationResult = validateAll(form);
      if (Object.keys(validationResult).length > 0) {
        setIsLoading(false);
        return;
      }
      const details: PackageDetail[] = [
        {
          id: "",
          packageId: 0,
          durationId: 20,
          duration: "Monthly",
          validityDays: Number(form.monthlyValidityDays),
          price: Number(form.monthlyPrice),
        },
        {
          id: "",
          packageId: 0,
          durationId: 21,
          duration: "Yearly",
          validityDays: Number(form.yearlyValidityDays),
          price: Number(form.yearlyPrice),
        },
      ];
      const payload = {
        title: form.title,
        name: form.name,
        description: form.description,
        numberOfUsers: Number(form.numberOfUsers),
        color: form.color,
        isIndividual: form.isIndividual,
        details,
      };
      if (isEdit && id) {
        await packagesService.update(id, payload);
        toast.success("Package updated successfully");
      } else {
        await packagesService.create(payload);
        toast.success("Package created successfully");
      }
      navigate(ROUTES.PACKAGES);
    } catch (e: any) {
      const friendly =
        "An error occurred on the server. Please contact the administrator.";
      setError(e.message ?? friendly);
      toast.error(e.message ?? friendly);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <MainLayout>
      <Card>
        <CardHeader
          title={
            isEdit ? "Edit Package" : isView ? "View Package" : "New Package"
          }
        />
        <CardBody>
          <Grid cols={2} gap="lg">
            <GridItem>
              <Input
                label="Title"
                name="title"
                value={form.title}
                onChange={handleChange}
                fullWidth
                error={errors.name}
                required
                disabled={isView}
                placeholder="Enter package title"
              />
            </GridItem>
            <GridItem>
              <Input
                label="Name"
                name="name"
                value={form.name}
                onChange={handleChange}
                fullWidth
                error={errors.name}
                required
                disabled={isView}
                placeholder="Enter package name"
              />
            </GridItem>
            <GridItem>
              <Input
                label="Number of Users"
                name="numberOfUsers"
                type="number"
                value={form.numberOfUsers}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                fullWidth
                error={errors.numberOfUsers}
                required
                disabled={isView}
                placeholder="Enter number of users"
                min="1"
              />
            </GridItem>
            <GridItem className="flex items-center mt-6 text-sm font-medium text-secondary-700 cursor-pointer select-none">
              <Checkbox
                label="Is Individual Package"
                name="isIndividual"
                checked={form.isIndividual}
                onChange={handleChecked}
                disabled={isView}
              />
            </GridItem>
            <GridItem>
              <ColorPicker
                label="Color"
                value={form.color || "#01878f"}
                onChange={handleColorChange("color")}
                showPresets={true}
                disabled={isView}
                presetColors={palettePresets}
                required
              />
            </GridItem>

            <GridItem colSpan={2}>
              <h4 className="text-sm font-semibold text-secondary-800 mb-2">
                Package details (per duration)
              </h4>
            </GridItem>
            <GridItem>
              <Input
                label="Monthly – Validity (days)"
                name="monthlyValidityDays"
                type="number"
                value={form.monthlyValidityDays}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                fullWidth
                error={errors.monthlyValidityDays}
                required
                disabled={isView}
                placeholder="e.g. 30"
                min="1"
              />
            </GridItem>
            <GridItem>
              <Input
                label="Monthly – Price"
                name="monthlyPrice"
                type="number"
                value={form.monthlyPrice}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                fullWidth
                error={errors.monthlyPrice}
                required
                disabled={isView}
                placeholder="0"
                min="0"
              />
            </GridItem>
            <GridItem>
              <Input
                label="Yearly – Validity (days)"
                name="yearlyValidityDays"
                type="number"
                value={form.yearlyValidityDays}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                fullWidth
                error={errors.yearlyValidityDays}
                required
                disabled={isView}
                placeholder="e.g. 365"
                min="1"
              />
            </GridItem>
            <GridItem>
              <Input
                label="Yearly – Price"
                name="yearlyPrice"
                type="number"
                value={form.yearlyPrice}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                fullWidth
                error={errors.yearlyPrice}
                required
                disabled={isView}
                placeholder="0"
                min="0"
              />
            </GridItem>

            <GridItem colSpan={2}>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Description
              </label>
              <SunEditor
                name="description"
                setContents={form.description}
                disable={isView}
                onChange={(content: any) => {
                  setForm((prev) => ({ ...prev, description: content }));
                }}
                height="300px"
                setOptions={{
                  buttonList: [
                    ["undo", "redo"],
                    ["formatBlock", "font", "fontSize"],
                    ["bold", "underline", "italic", "strike"],
                    ["fontColor", "hiliteColor", "textStyle"],
                    ["removeFormat"],
                    ["align", "list", "table"],
                    ["link", "image", "video"],
                    ["fullScreen", "showBlocks", "codeView", "preview"],
                  ],
                }}
              />
            </GridItem>
          </Grid>
        </CardBody>
      </Card>

      <div className="flex justify-end gap-3 mt-6">
        <Button
          variant="outline"
          onClick={() => navigate(ROUTES.PACKAGES)}
          disabled={isLoading}
        >
          Back
        </Button>
        {!isView && (
          <Button
            variant="primary"
            onClick={onSubmit}
            isLoading={isLoading}
            disabled={isLoading}
          >
            Save
          </Button>
        )}
      </div>
    </MainLayout>
  );
};
